import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2, ArrowRight, Lock, User } from 'lucide-react';
import { toast } from 'sonner';
import { Kicker, SmchsLayout } from '@/components/smchs/Shell';
import LibraryFigures from '@/components/smchs/LibraryFigures';
import { useAuth } from '@/hooks/useAuth';
import { TwoFactorRequiredError } from '@/services/user-api';

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading: authLoading, user } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    two_fa_token: '',
  });
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isAuthenticated && !authLoading && user) {
      const role: string | undefined = user.user_type || user.role;
      const roles: string[] = Array.isArray(user.roles) ? user.roles : [];
      const isLibrarian = role === 'librarian' || roles.includes('librarian');
      const isAdmin = role === 'admin' || roles.includes('admin');
      if (isLibrarian && !isAdmin) navigate('/librarian');
      else if (isAdmin) navigate('/admin');
      else if (role === 'staff' || role === 'faculty') navigate('/admin');
      else if (role === 'student') navigate('/student');
      else navigate('/catalog');
    }
  }, [isAuthenticated, authLoading, user, navigate]);

  const validateForm = (): boolean => {
    const next: Record<string, string> = {};
    if (!formData.username.trim()) next.username = 'Username is required';
    if (!formData.password) next.password = 'Password is required';
    else if (formData.password.length < 3) next.password = 'Password must be at least 3 characters';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      await login({
        username: formData.username,
        password: formData.password,
        ...(needsTwoFactor && formData.two_fa_token ? { two_fa_token: formData.two_fa_token } : {}),
      });
      toast.success('Welcome back. Redirecting…');
    } catch (error: any) {
      if (error instanceof TwoFactorRequiredError) {
        // 202: credentials accepted, ask for the authenticator code and resubmit.
        setNeedsTwoFactor(true);
        toast.message('Enter the 6-digit code from your authenticator app.');
        return;
      }
      let message = 'Login failed. Please try again.';
      if (error?.message) message = error.message;
      else if (error?.toString().includes('Invalid time value')) message = 'Authentication token error. Please try again.';
      toast.error(message);
      if (!needsTwoFactor) setFormData((p) => ({ ...p, password: '' }));
    } finally {
      setIsLoading(false);
    }
  };

  const update = (field: string, value: string | boolean) => {
    setFormData((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: '' }));
  };

  return (
    <SmchsLayout>
      <section className="relative overflow-hidden">
        {/* Backdrop: matches PageHero glow + masked grid */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_55%_at_50%_-5%,rgba(30,45,92,0.14),transparent_60%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:56px_56px] [mask-image:radial-gradient(ellipse_60%_70%_at_50%_10%,black,transparent_70%)] opacity-40" />
        </div>

        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-14 md:py-20 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left: editorial column */}
          <div className="lg:col-span-7">
            <Kicker label="Member sign in" />
            <h1 className="mt-5 font-display font-[900] text-[2.25rem] sm:text-[3rem] md:text-[3.75rem] leading-[0.98] tracking-[-0.03em] text-[var(--ink)] max-w-[18ch]">
              Welcome back to the{' '}
              <span className="font-serif italic font-normal text-[var(--primary)]">library</span>.
            </h1>
            <p className="mt-6 max-w-[52ch] text-[1rem] md:text-[1.05rem] leading-[1.55] text-[var(--ink-soft)]">
              Sign in with your SMCHS account to borrow books, manage loans, join book clubs, and
              continue your reading challenges.
            </p>

            <LibraryFigures />
          </div>

          {/* Right: form card */}
          <div className="w-full max-w-[560px] lg:max-w-none lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="relative bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-7 md:p-9 shadow-[0_30px_60px_-30px_rgba(10,18,40,0.18)]"
            >
              <div className="absolute -top-px left-7 right-7 h-px bg-gradient-to-r from-transparent via-[var(--gold)] to-transparent" />

              <div className="flex items-center justify-between">
                <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--muted)]">
                  Sign in
                </div>
                <Link
                  to="/"
                  className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
                >
                  ← Home
                </Link>
              </div>

              <h2 className="mt-5 font-display font-bold text-[1.5rem] text-[var(--ink)] tracking-[-0.02em]">
                Account access
              </h2>
              <p className="mt-1.5 text-[13px] text-[var(--ink-soft)]">
                Enter your SMCHS credentials to continue.
              </p>

              <form className="mt-7 space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label
                    htmlFor="username"
                    className="block text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--muted)] mb-2"
                  >
                    Username
                  </label>
                  <div className="relative">
                    <User className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted)]" />
                    <input
                      id="username"
                      type="text"
                      autoComplete="username"
                      placeholder="your.username"
                      value={formData.username}
                      onChange={(e) => update('username', e.target.value)}
                      disabled={isLoading}
                      className={`w-full h-11 pl-10 pr-3 rounded-lg bg-[var(--bg)] border text-[14px] text-[var(--ink)] placeholder:text-[var(--muted)] outline-none transition-colors focus:bg-[var(--surface)] focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/15 ${
                        errors.username ? 'border-red-500/70' : 'border-[var(--border)]'
                      }`}
                    />
                  </div>
                  {errors.username && (
                    <p className="mt-1.5 text-[12px] text-red-500">{errors.username}</p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label
                      htmlFor="password"
                      className="block text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--muted)]"
                    >
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => toast.info('Password reset is coming soon.')}
                      className="text-[11px] font-mono uppercase tracking-[0.15em] text-[var(--primary)] hover:opacity-80 transition-opacity"
                    >
                      Forgot?
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted)]" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => update('password', e.target.value)}
                      disabled={isLoading}
                      className={`w-full h-11 pl-10 pr-11 rounded-lg bg-[var(--bg)] border text-[14px] text-[var(--ink)] placeholder:text-[var(--muted)] outline-none transition-colors focus:bg-[var(--surface)] focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/15 ${
                        errors.password ? 'border-red-500/70' : 'border-[var(--border)]'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      disabled={isLoading}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md flex items-center justify-center text-[var(--muted)] hover:text-[var(--ink)] hover:bg-[var(--surface-2)] transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1.5 text-[12px] text-red-500">{errors.password}</p>
                  )}
                </div>

                {needsTwoFactor && (
                  <div>
                    <label htmlFor="two_fa_token" className="block text-[13px] font-medium text-[var(--ink)] mb-1.5">
                      Authenticator code
                    </label>
                    <input
                      id="two_fa_token"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      value={formData.two_fa_token}
                      onChange={(e) => update('two_fa_token', e.target.value.trim())}
                      disabled={isLoading}
                      placeholder="123456"
                      className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface)] px-3.5 py-2.5 text-[14px] text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30"
                    />
                    <p className="mt-1.5 text-[12px] text-[var(--muted)]">A backup code works here too.</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || authLoading}
                  className="group w-full h-11 rounded-lg bg-[var(--ink)] text-[var(--paper)] font-display font-semibold text-[14px] tracking-[-0.005em] flex items-center justify-center gap-2 hover:brightness-110 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_8px_24px_-10px_rgba(10,18,40,0.45)]"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    <>
                      Sign in
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>

                <div className="pt-2 border-t border-[var(--border)] text-center">
                  <p className="pt-4 text-[13px] text-[var(--ink-soft)]">
                    Don't have an account?{' '}
                    <Link
                      to="/signup"
                      className="font-medium text-[var(--primary)] hover:opacity-80 transition-opacity"
                    >
                      Get a library card
                    </Link>
                  </p>
                  <p className="pt-2 text-[13px] text-[var(--ink-soft)]">
                    <Link
                      to="/reset-password"
                      className="font-medium text-[var(--primary)] hover:opacity-80 transition-opacity"
                    >
                      Forgotten your password?
                    </Link>
                  </p>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </SmchsLayout>
  );
};

export default SignIn;
